import { useState, useMemo, useCallback, useRef } from "react";
import { Page } from "@iq/models";
import { StoreCatalogCard } from "../modelsTesting";
import "../styles/catalog.css";
import { FiPlus } from "react-icons/fi";
import CreateProductModal from "../modals/CreateProduct/CreateProductModal";
import AddAttributesModal from "../modals/AddAttributes/AddAttributesModal";
import RenameProduct from "../modals/RenameProduct/RenameProduct";
import { deleteProduct } from "../services/productService";
import useIsSmallScreen from "../hooks/useIsSmallScreen";
import useProductCatalogStore from "../states/productCatalog";

function Catalog({ onOpenProduct = () => {} }) {
    const products = useProductCatalogStore((state) => state.products);
    const loadingProducts = useProductCatalogStore((state) => state.loading);
    const error = useProductCatalogStore((state) => state.error);
    const fetchProducts = useProductCatalogStore((state) => state.fetchProducts);
    const [searchQuery, setSearchQuery] = useState("");
    const [isCreateProductModalOpen, setIsCreateProductModalOpen] = useState(false);
    const [attributesTarget, setAttributesTarget] = useState(null);
    const [renameTarget, setRenameTarget] = useState(null);
    const deletingRef = useRef(false);

    const handleDeleteProduct = async (product) => {
        if (deletingRef.current) return;
        const productId = product.id || product.ID;
        if (!window.confirm(`Delete "${product.name || "this service"}"? This cannot be undone.`)) return;

        deletingRef.current = true;
        try {
            await deleteProduct(productId);
        } catch (deleteError) {
            window.alert(deleteError?.message || "Failed to delete product.");
            deletingRef.current = false;
            return;
        }
        try {
            await fetchProducts();
            const refreshError = useProductCatalogStore.getState().error;
            if (refreshError) {
                window.alert(`Service deleted, but the catalog could not refresh: ${refreshError}`);
            }
        } finally {
            deletingRef.current = false;
        }
    };

    const handleOpenCreateProductModal = useCallback(() => {
        setIsCreateProductModalOpen(true);
    }, []);

    const handleProductCreated = useCallback(async (createdProduct, { failedImageUploads = [] } = {}) => {
        setIsCreateProductModalOpen(false);

        const createdProductId = Number(createdProduct?.id ?? createdProduct?.ID ?? 0);
        if (createdProductId > 0) {
            setAttributesTarget({ id: createdProductId, name: createdProduct?.name || "" });
        }

        await fetchProducts();

        if (failedImageUploads.length > 0) {
            console.warn("Service created, but some images failed to upload.", failedImageUploads);
        }
    }, [fetchProducts]);

    const filteredProducts = useMemo(() => {
            const normalizedQuery = searchQuery.trim().toLowerCase();
            if (!normalizedQuery) {
                    return products;
            }

            return products.filter((product) => {
                    const normalizedName = String(product?.name || "").toLowerCase();
                    return normalizedName.includes(normalizedQuery);
            });
    }, [products, searchQuery]);

    const isSmallScreen = useIsSmallScreen();

    return (
        <Page>
            <div className="catalogHeader">
                <input
                    className="catalogSearchInput"
                    type="text"
                    placeholder="Search catalog"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                {!isSmallScreen && (
                    <button
                        className="btn catalogCreateProductButton"
                        onClick={handleOpenCreateProductModal}
                    >
                        <span>Create Service</span>
                        <FiPlus size={18}/>
                    </button>
                )}
            </div>
            {loadingProducts ? <p>Loading products...</p> : null}
            {error ? <p>{error}</p> : null}
            {!loadingProducts && !error ? (
                <div className="catalogCardsContainer">
                    {filteredProducts.map((product) => (
                        <StoreCatalogCard
                            key={product.id}
                            name={product.name}
                            description={product.description}
                            image={product.image}
                            attributeCount={product.attribute_count ?? product.variant_count ?? 0}
                            activeAttributeCount={product.active_attribute_count ?? product.active_variant_count ?? 0}
                            pricing={product.pricing}
                            onClick={() => onOpenProduct(product.id || product.ID)}
                            onRename={() => setRenameTarget({ id: product.id || product.ID, name: product.name })}
                            onDelete={() => handleDeleteProduct(product)}
                        />
                    ))}
                    {filteredProducts.length === 0 ? <p>No products match your search.</p> : null}
                </div>
            ) : null}

            <RenameProduct
                isOpen={!!renameTarget}
                onClose={() => setRenameTarget(null)}
                productId={renameTarget?.id}
                currentName={renameTarget?.name ?? ""}
                onRenameSuccess={() => {
                    setRenameTarget(null);
                    fetchProducts();
                }}
            />
            <CreateProductModal
                isOpen={isCreateProductModalOpen}
                onClose={() => setIsCreateProductModalOpen(false)}
                onCreated={handleProductCreated}
            />
            <AddAttributesModal
                isOpen={!!attributesTarget}
                productId={attributesTarget?.id}
                productName={attributesTarget?.name ?? ""}
                onClose={() => setAttributesTarget(null)}
                onSaved={async () => {
                    setAttributesTarget(null);
                    await fetchProducts();
                }}
            />
        </Page>
    )
}

export default Catalog
